/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineMail,
  HiOutlineRefresh,
} from "react-icons/hi";
import { HiArrowUpRight } from "react-icons/hi2";

import { BreadCrumbComponent } from "../../ui/BreadCrumbComponent";
import { Avatar, PageHeader, cn } from "../../ui";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useStaticContext } from "../../context/StaticContext";
import { addOneLevel } from "../../lib/breadCrumb/BreadCrumb";
import { pushLevel, setAdminOption } from "../../lib";
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  MODULES,
} from "../dashboard/modules";
import type { ModuleCategory, ModuleItem } from "../dashboard/types";
import { getType } from "./helper";
import { getPageRouteFromKey } from "./routesHelper";

// ─── Types & constants ────────────────────────────────────────────────
type StatEntry = {
  key: string;
  value: number;
};

type StatsPayload = Record<string, any>;

const IGNORED_KEYS = new Set([
  "email",
  "admin_id",
  "last_name",
  "first_name",
  "phonenumber",
]);

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT = MONTHS.map((m) => m.slice(0, 3));

const toStatEntries = (stats: StatsPayload | null | undefined): StatEntry[] => {
  if (!stats || typeof stats !== "object") return [];
  const out: StatEntry[] = [];
  for (const key of Object.keys(stats)) {
    if (IGNORED_KEYS.has(key)) continue;
    const v = stats[key];
    out.push({ key, value: typeof v === "number" ? v : 0 });
  }
  return out;
};

// Lookup from stat key → module (for icon/category). Falls back to a catch-all.
const MODULE_BY_STAT_KEY = new Map<string, ModuleItem>();
for (const m of MODULES) MODULE_BY_STAT_KEY.set(m.statKey, m);

const humanise = (key: string) => key.replace(/_/g, " ");

// ─── Period picker ───────────────────────────────────────────────────
type PeriodProps = {
  year: number;
  month: number; // 1-12
  onChange: (next: { year: number; month: number }) => void;
};

const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;

const PeriodPicker: React.FC<PeriodProps> = ({ year, month, onChange }) => {
  const years = Array.from({ length: 4 }, (_, i) => thisYear - 3 + i);
  const isCurrent = year === thisYear && month === thisMonth;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineCalendar className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[13px] font-semibold tracking-tight text-gray-900">
              Period
            </p>
            <p className="text-[11px] text-gray-500">
              Filter contributions by month and year.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange({ year: thisYear, month: thisMonth })}
          disabled={isCurrent}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-semibold transition-colors",
            isCurrent
              ? "cursor-default border-gray-200 bg-gray-50 text-gray-400"
              : "border-gray-200 bg-white text-gray-700 hover:border-brand/40 hover:text-brand",
          )}
        >
          <HiOutlineRefresh className="h-3.5 w-3.5" />
          This month
        </button>
      </header>

      <div className="space-y-4 px-4 py-4">
        <div>
          <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
            Year
          </p>
          <div className="flex flex-wrap gap-2">
            {years.map((y) => {
              const active = y === year;
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => onChange({ year: y, month })}
                  aria-pressed={active}
                  className={cn(
                    "inline-flex items-center rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold tracking-tight transition-colors",
                    active
                      ? "border-brand bg-brand text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                  )}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
            Month
          </p>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-12">
            {MONTH_SHORT.map((label, idx) => {
              const m = idx + 1;
              const active = m === month;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => onChange({ year, month: m })}
                  aria-pressed={active}
                  title={MONTHS[idx]}
                  className={cn(
                    "inline-flex items-center justify-center rounded-lg border px-2 py-2 text-[12px] font-semibold tracking-tight transition-colors",
                    active
                      ? "border-brand bg-brand text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Stat card ───────────────────────────────────────────────────────
type StatCardProps = {
  module: ModuleItem | null;
  fallbackKey: string;
  value: number;
  onOpen: () => void;
  route: string;
};

const StatCard: React.FC<StatCardProps> = ({
  module,
  fallbackKey,
  value,
  onOpen,
  route,
}) => {
  const label = module?.name ?? humanise(fallbackKey);
  const isEmpty = value === 0;

  return (
    <Link
      to={route}
      onClick={onOpen}
      className={cn(
        "group relative flex min-h-[130px] flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 transition-all",
        "hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_10px_30px_-12px_rgba(0,145,145,0.25)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-brand transition-transform duration-300 ease-out group-hover:scale-x-100"
      />

      <div className="flex items-start justify-between gap-3">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/15 transition-colors group-hover:bg-brand group-hover:text-white group-hover:ring-brand"
        >
          {module?.icon ?? <HiOutlineChartBar className="h-5 w-5" />}
        </span>
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 opacity-0 transition-all duration-300 group-hover:border-brand/30 group-hover:bg-brand/10 group-hover:text-brand group-hover:opacity-100"
        >
          <HiArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>

      <div className="mt-auto min-w-0">
        <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
          {label}
        </p>
        <p
          className={cn(
            "mt-1 truncate text-[28px] font-semibold leading-none tracking-tight tabular-nums",
            isEmpty ? "text-gray-300" : "text-gray-900",
          )}
        >
          {value.toLocaleString()}
        </p>
      </div>
    </Link>
  );
};

// ─── Main page ───────────────────────────────────────────────────────
const Overview: React.FC = () => {
  const { getStats, stats } = useStaticContext() as any;
  const { sysAdmin, setSingleAdmin } = useAdminContext();

  const fullName =
    `${(sysAdmin as any)?.first_name ?? ""} ${(sysAdmin as any)?.last_name ?? ""}`
      .trim() || "Admin";
  const email = (sysAdmin as any)?.email ?? "";
  const admin_id = (sysAdmin as any)?.admin_id;

  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(thisMonth);
  const [loading, setLoading] = useState(false);

  const load = async (y: number, m: number) => {
    setLoading(true);
    await getStats({ year: y, month: m, data: { ids: [admin_id] } });
    setLoading(false);
  };

  useEffect(() => {
    if (!stats) load(year, month);
  }, []);

  const changePeriod = (next: { year: number; month: number }) => {
    setYear(next.year);
    setMonth(next.month);
    load(next.year, next.month);
  };

  const entries = useMemo<StatEntry[]>(() => toStatEntries(stats), [stats]);
  const total = useMemo(
    () => entries.reduce((sum, e) => sum + e.value, 0),
    [entries],
  );
  const activeCategories = useMemo(
    () =>
      entries
        .filter((e) => e.value > 0)
        .map((e) => MODULE_BY_STAT_KEY.get(e.key)?.category)
        .filter((c): c is ModuleCategory => Boolean(c)),
    [entries],
  );

  const groupedByCategory = useMemo(() => {
    const buckets = new Map<
      ModuleCategory | "other",
      { entry: StatEntry; module: ModuleItem | null }[]
    >();
    for (const cat of CATEGORY_ORDER) buckets.set(cat, []);
    buckets.set("other", []);
    for (const e of entries) {
      const module = MODULE_BY_STAT_KEY.get(e.key) ?? null;
      const cat = (module?.category ?? "other") as ModuleCategory | "other";
      const list = buckets.get(cat) ?? [];
      list.push({ entry: e, module });
      buckets.set(cat, list);
    }
    return [
      ...CATEGORY_ORDER.map((cat) => ({
        category: cat as ModuleCategory,
        meta: CATEGORY_META[cat as ModuleCategory],
        items: (buckets.get(cat) ?? []).sort(
          (a, b) => b.entry.value - a.entry.value,
        ),
      })),
      {
        category: "other" as const,
        meta: {
          title: "Other",
          hint: "Unclassified activity.",
          dot: "bg-brand",
        },
        items: (buckets.get("other") ?? []).sort(
          (a, b) => b.entry.value - a.entry.value,
        ),
      },
    ].filter((b) => b.items.length > 0);
  }, [entries]);

  const handleOpen = (key: string) => {
    setSingleAdmin({
      ids: [admin_id],
      year,
      month,
      type: getType({ field: key }),
      route: "/content/stats",
    } as any);
    addOneLevel({
      id: 0,
      name: fullName,
      route: "/levels/first",
      level: "",
      country_id: 0,
      data: {
        data: {
          name: "",
          country_id: 0,
          first_level_id: 0,
          second_level_id: 0,
          third_level_id: 0,
          fourth_level_id: 0,
          level: "",
        },
      },
    } as any);
    pushLevel({
      level: {
        id: 0,
        name: "",
        level: "All",
        route: "/levels/first",
        data: null,
      },
    } as any);
    setAdminOption((sysAdmin as any)?.admin_id);
  };

  return (
    <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <BreadCrumbComponent />
        <PageHeader
          title="Content Stats"
          subtitle="Track what you've contributed across every section, month by month."
          icon={<HiOutlineChartBar className="h-5 w-5" />}
        />

        {/* Hero card: admin identity + period summary */}
        <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-20 right-32 h-40 w-40 rounded-full bg-brand/[0.07] blur-3xl"
          />

          <div className="relative grid gap-6 px-6 py-6 sm:px-8 sm:py-7 lg:grid-cols-[1.1fr_auto] lg:items-center lg:gap-10">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand">
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
                </span>
                Contributions
              </div>

              <div className="mt-4 flex items-center gap-3">
                <Avatar
                  firstName={(sysAdmin as any)?.first_name}
                  lastName={(sysAdmin as any)?.last_name}
                  size="lg"
                />
                <div className="min-w-0">
                  <h1 className="truncate text-[22px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[24px]">
                    {fullName}
                  </h1>
                  {email ? (
                    <p className="mt-0.5 inline-flex items-center gap-1.5 truncate text-[12.5px] text-gray-500">
                      <HiOutlineMail className="h-3.5 w-3.5" />
                      {email}
                    </p>
                  ) : null}
                </div>
              </div>

              <p className="mt-3 text-[13px] text-gray-500">
                {MONTHS[month - 1]} {year} ·{" "}
                {activeCategories.length > 0
                  ? `${new Set(activeCategories).size} active ${new Set(activeCategories).size === 1 ? "area" : "areas"}`
                  : "Nothing yet this period"}
              </p>
            </div>

            <div className="flex items-center gap-3 rounded-2xl bg-brand/[0.04] px-4 py-3.5 ring-1 ring-inset ring-brand/15">
              <span
                aria-hidden
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand text-white"
              >
                <HiOutlineChartBar className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                  Total items
                </p>
                <p className="mt-0.5 truncate text-[20px] font-semibold leading-none tracking-tight tabular-nums text-gray-900">
                  {total.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </section>

        <PeriodPicker year={year} month={month} onChange={changePeriod} />

        {/* Stats grid */}
        {loading || !stats ? (
          <LoadingSkeleton />
        ) : groupedByCategory.length === 0 ? (
          <EmptyState label={`${MONTHS[month - 1]} ${year}`} />
        ) : (
          <div className="space-y-8">
            {groupedByCategory.map(({ category, meta, items }) => (
              <section key={category} aria-label={meta.title}>
                <header className="mb-3 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="h-4 w-[3px] rounded-full bg-brand"
                      />
                      <h3 className="truncate text-[15px] font-semibold tracking-tight text-gray-900">
                        {meta.title}
                      </h3>
                      <span className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10.5px] font-semibold tabular-nums text-gray-600">
                        {items.length}
                      </span>
                    </div>
                    <p className="mt-1 truncate pl-[14px] text-[12px] text-gray-500">
                      {meta.hint}
                    </p>
                  </div>
                  <p className="text-[11.5px] text-gray-500">
                    <span className="font-semibold tabular-nums text-gray-900">
                      {items
                        .reduce((sum, i) => sum + i.entry.value, 0)
                        .toLocaleString()}
                    </span>{" "}
                    items
                  </p>
                </header>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {items.map(({ entry, module }) => (
                    <StatCard
                      key={entry.key}
                      module={module}
                      fallbackKey={entry.key}
                      value={entry.value}
                      route={getPageRouteFromKey({ key: entry.key })}
                      onOpen={() => handleOpen(entry.key)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-8">
    {[0, 1].map((section) => (
      <div key={section}>
        <div className="mb-3 flex items-center gap-2.5">
          <span className="h-4 w-[3px] rounded-full bg-brand/30" />
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="min-h-[130px] animate-pulse rounded-2xl border border-gray-200 bg-white"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-14 text-center">
    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
      <HiOutlineChartBar className="h-4 w-4" />
    </div>
    <p className="mt-4 text-[13px] font-medium text-gray-900">
      No contributions for {label} yet.
    </p>
    <p className="mt-1 text-[12px] text-gray-500">
      Pick another month, or start publishing content — it'll show up here.
    </p>
  </div>
);

export default Overview;
